import {
  addDefaultProps,
  addDisplayName,
  addHandlers,
  addProps,
  flowMax,
} from 'ad-hok'
import { FC } from 'react'
import { createUseStyles } from 'react-jss'
import { trackEvent } from 'shared/src/utils/analytics'

import addBookmarkHeartIconStandalone from 'assets/add-bookmark-heart.svg'
import removeBookmarkHeartIconStandalone from 'assets/remove-bookmark-heart.svg'
import removeBookmarkIcon from 'assets/bookmark-remove.svg'
import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { getRoundupStyle, RoundupStyle } from 'utils/testing'
import addBookmarkHeartIconProductTile from '../assets/add-bookmark-heart-mini.png'
import addBookmarkMiniIconProductTile from '../assets/bookmark-add-mini.svg'
import addBookmarkIconProductTile from '../assets/add-bookmark-mini.png'
import addBookmarkIcon from '../assets/bookmark-add.svg'
import removeBookmarkHeartIconProductTile from '../assets/remove-bookmark-heart-mini.png'
import removeBookmarkMiniIconProductTile from '../assets/bookmark-remove-mini.svg'
import removeBookmarkIconProductTile from '../assets/remove-bookmark-mini.png'
import removeBookmarkIconStandalone from '../assets/remove-bookmark.png'
import { addShowRegisterContext } from './../utils/register'
import { Button } from './Button'
import { ColorButton } from './ColorButton'
import { addBookmarkContext } from './bookmarks'
import {
  addNotificationContext,
  createAddedToRoundupNotification,
} from './notifications'

type Mode = 'standalone' | 'productTile'

const ICONS = {
  mini: {
    standalone: {
      add: addBookmarkIcon,
      remove: removeBookmarkIcon,
    },
    productTile: {
      add: addBookmarkMiniIconProductTile,
      remove: removeBookmarkMiniIconProductTile,
    },
  },
  lassos: {
    standalone: {
      add: addBookmarkIcon,
      remove: removeBookmarkIconStandalone,
    },
    productTile: {
      add: addBookmarkIconProductTile,
      remove: removeBookmarkIconProductTile,
    },
  },
  heart: {
    standalone: {
      add: addBookmarkHeartIconStandalone,
      remove: removeBookmarkHeartIconStandalone,
    },
    productTile: {
      add: addBookmarkHeartIconProductTile,
      remove: removeBookmarkHeartIconProductTile,
    },
  },
}

interface Props {
  mode?: Mode
  mediaId?: number
  userListId?: number
  creatorId?: number
  productId: number
  onAddBookmark?: () => void
  onRemoveBookmark?: () => void
  roundupStyle?: RoundupStyle
  className?: string
}

const useStyles = (mode: Mode, roundupStyle: RoundupStyle) =>
  createUseStyles({
    image: {
      width: mode === 'productTile' && roundupStyle === 'mini' ? 8 : 43,
      height: mode === 'productTile' && roundupStyle === 'mini' ? 20 : 43,
    },
  })

export const BookmarkButton: FC<Props> = flowMax(
  addDisplayName('BookmarkButton'),
  addDefaultProps({
    mode: 'standalone',
  }),
  addNotificationContext,
  addShowRegisterContext,
  addBookmarkContext,
  addProps(({ getBookmark, productId }) => ({
    bookmark: getBookmark(productId),
  })),
  addTranslationHelpers,
  addHandlers({
    toggleBookmark:
      ({
        mediaId,
        userListId,
        productId,
        bookmark,
        addBookmark,
        removeBookmark,
        creatorId,
        showNotification,
        showRegisterIfNotAuthenticated,
        onAddBookmark,
        onRemoveBookmark,
        bookmarkAfterLoggingIn,
        t,
      }) =>
      () => {
        if (showRegisterIfNotAuthenticated('bookmark')) {
          if (!bookmark) {
            bookmarkAfterLoggingIn({
              productId,
              mediaId,
              userListId,
            })
          }

          return
        }

        if (bookmark) {
          removeBookmark(productId)
          onRemoveBookmark?.()
        } else {
          addBookmark({
            productId,
            mediaId,
            userListId,
          })
          showNotification(createAddedToRoundupNotification(t))
          trackEvent(
            {
              category: 'product',
              action: 'bookmark',
            },
            {
              media_id: mediaId,
              user_list_id: userListId,
              product_id: productId,
              creator_id: creatorId,
            }
          )
          onAddBookmark?.()
        }
      },
  }),
  addProps(({ mode, roundupStyle }) => ({
    icons: ICONS[roundupStyle || getRoundupStyle()][mode],
  })),
  addProps(({ icons, bookmark, t, mode, roundupStyle }) => ({
    icon: bookmark ? icons.remove : icons.add,
    iconAlt: bookmark ? t('bookmark.removeAlt') : t('bookmark.addAlt'),
    backgroundColorForStandalone:
      bookmark && getRoundupStyle() === 'lassos' ? '#403F3F' : '#CBA67A',
    classes: useStyles(mode, roundupStyle || getRoundupStyle())(),
    roundupStyle: roundupStyle ? roundupStyle : getRoundupStyle(),
  })),
  ({
    mode,
    icon,
    iconAlt,
    backgroundColorForStandalone,
    toggleBookmark,
    className,
    classes,
    roundupStyle,
  }) =>
    mode === 'standalone' ? (
      roundupStyle === 'mini' ? (
        <Button className={className} onClick={toggleBookmark}>
          <img
            src={icon}
            alt={iconAlt}
            className={classes.image}
            style={{ width: 56, height: 56 }}
          />
        </Button>
      ) : (
        <ColorButton
          icon={icon}
          iconAlt={iconAlt}
          iconWidth={128 / 3}
          backgroundColor={backgroundColorForStandalone}
          textColor="white"
          onClick={toggleBookmark}
          className={className}
        />
      )
    ) : (
      <Button className={className} onClick={toggleBookmark}>
        <img src={icon} alt={iconAlt} className={classes.image} />
      </Button>
    )
)
